/**
 * Pallasite ghost replay (kind 30763).
 *
 * v1 = score-pacing: 1Hz sample of the player's score over the run, encoded
 * as a packed binary stream of uint32 score values. Used to render the
 * leader-chip during play ("LEADER 12,450 · YOU +200 @ 5:30").
 *
 * v2 (planned) extends frames with ship pose for the daily-mode overlay.
 * Magic + ver bytes let v1 consumers ignore v2 events cleanly.
 *
 * Wire format (v1):
 *   header  ['G','H', ver=1, fps=1, frameCount uint32 LE]   8 bytes
 *   body    frameCount × uint32 LE score                    N × 4 bytes
 *
 * Decoded sample timestamps are reconstructed as `i / fps` seconds.
 */

import type { SignetSession, NostrEvent } from 'signet-login';
import { GAME_ID } from './auth.js';
import { WORLD_W, WORLD_H, type GhostSample, type GhostPoseSample } from './types.js';
import { getActiveRelays } from './relays.js';
import { EXPERIMENTAL_RELAYS } from './credits.js';
import { uploadReplay } from './faucet.js';

/** Publish + fetch share the same relay set so a watcher always queries
 *  the same URLs the player published to. Without this union, the
 *  watcher's getActiveRelays() (its localStorage on watch.pallasite.app)
 *  can diverge from the player's getActiveRelays() (their localStorage
 *  on pallasite.app) and a published event won't be findable. We always
 *  include EXPERIMENTAL_RELAYS (our own relay.trotters.cc) so even with
 *  zero overlap in user-customised relays there's a guaranteed shared
 *  endpoint. */
function ghostRelaySet(override?: readonly string[]): readonly string[] {
  if (override && override.length > 0) return override;
  const active = getActiveRelays();
  const merged = new Set<string>([...active, ...EXPERIMENTAL_RELAYS]);
  return Array.from(merged);
}

export const GHOST_KIND = 30763;
/** kind 30764 — full-world compressed replay. The kind 30763 ghost
 *  carries pose-only samples; 30764 carries the entire wire-format
 *  frame buffer (asteroids, UFOs, mines, bullets, coins, powerups,
 *  SFX events) so the replay theatre can render the full world the
 *  player saw, not just their ship trajectory.
 *
 *  Content is base64(gzip(JSON [ReplayFrameRaw, ...])). Player buffers
 *  every 3 Hz frame in-memory during the run; on claim, the buffer is
 *  flushed into this event alongside the kind 30763 ghost. */
export const REPLAY_KIND = 30764;
export const GHOST_FPS_V1 = 1;
export const GHOST_FPS_V2 = 4;
const MAGIC_G = 0x47;
const MAGIC_H = 0x48;
/** Coordinate fixed-point scale: float world-pixels → uint16 [0, 65535]. */
const POS_SCALE_X = 65535 / WORLD_W;
const POS_SCALE_Y = 65535 / WORLD_H;
/** Rotation fixed-point: int16 = round(radians * 10000). Range ±3.27 rad
 *  comfortably covers full -π..π (3.14). */
const ROT_SCALE = 10000;

export interface GhostRun {
  pubkey: string;
  score: number;
  wave: number;
  seed?: string;
  encoding: 'ghost-v1' | 'ghost-v2';
  fps: number;
  /** Score-pacing samples — always present. v2 reconstructs from per-frame
   *  scores; v1 reads them directly. */
  samples: GhostSample[];
  /** Pose stream — present only on v2 events. Used by the daily-mode
   *  ship overlay; consumers without overlay rendering can ignore this. */
  poseSamples?: GhostPoseSample[];
  /** Total wall-time of the run, ms — taken from `duration` tag if present, else last sample.t. */
  durationMs: number;
  eventId: string;
}

export function encodeGhostV1(samples: readonly GhostSample[]): string {
  const header = 8;
  const body = samples.length * 4;
  const buf = new ArrayBuffer(header + body);
  const view = new DataView(buf);
  view.setUint8(0, MAGIC_G);
  view.setUint8(1, MAGIC_H);
  view.setUint8(2, 1);
  view.setUint8(3, GHOST_FPS_V1);
  view.setUint32(4, samples.length, true);
  for (let i = 0; i < samples.length; i++) {
    view.setUint32(header + i * 4, Math.max(0, samples[i].score) >>> 0, true);
  }
  return bufferToBase64(buf);
}

export function decodeGhostV1(b64: string): GhostSample[] | null {
  let buf: ArrayBuffer;
  try { buf = base64ToBuffer(b64); } catch { return null; }
  if (buf.byteLength < 8) return null;
  const view = new DataView(buf);
  if (view.getUint8(0) !== MAGIC_G || view.getUint8(1) !== MAGIC_H) return null;
  if (view.getUint8(2) !== 1) return null;
  const fps = view.getUint8(3);
  if (fps < 1 || fps > 30) return null;
  const count = view.getUint32(4, true);
  if (count > 100_000) return null;
  if (buf.byteLength < 8 + count * 4) return null;
  const intervalMs = Math.round(1000 / fps);
  const out: GhostSample[] = [];
  for (let i = 0; i < count; i++) {
    out.push({ t: i * intervalMs, score: view.getUint32(8 + i * 4, true) });
  }
  return out;
}

/** v2 frame layout (11 bytes per frame):
 *    score   uint32 LE
 *    x_q     uint16 LE  (world.x * 65535 / WORLD_W)
 *    y_q     uint16 LE  (world.y * 65535 / WORLD_H)
 *    rot_q   int16  LE  (radians * 10000)
 *    flags   uint8     (bit 0 alive, bit 1 thrusting)
 */
export function encodeGhostV2(samples: readonly GhostPoseSample[]): string {
  const header = 8;
  const body = samples.length * 11;
  const buf = new ArrayBuffer(header + body);
  const view = new DataView(buf);
  view.setUint8(0, MAGIC_G);
  view.setUint8(1, MAGIC_H);
  view.setUint8(2, 2);
  view.setUint8(3, GHOST_FPS_V2);
  view.setUint32(4, samples.length, true);
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    const off = header + i * 11;
    view.setUint32(off, Math.max(0, s.score) >>> 0, true);
    const xq = Math.max(0, Math.min(65535, Math.round(s.x * POS_SCALE_X)));
    const yq = Math.max(0, Math.min(65535, Math.round(s.y * POS_SCALE_Y)));
    const rotq = Math.max(-32768, Math.min(32767, Math.round(s.rot * ROT_SCALE)));
    view.setUint16(off + 4, xq, true);
    view.setUint16(off + 6, yq, true);
    view.setInt16(off + 8, rotq, true);
    view.setUint8(off + 10, s.flags & 0xff);
  }
  return bufferToBase64(buf);
}

export function decodeGhostV2(b64: string): GhostPoseSample[] | null {
  let buf: ArrayBuffer;
  try { buf = base64ToBuffer(b64); } catch { return null; }
  if (buf.byteLength < 8) return null;
  const view = new DataView(buf);
  if (view.getUint8(0) !== MAGIC_G || view.getUint8(1) !== MAGIC_H) return null;
  if (view.getUint8(2) !== 2) return null;
  const fps = view.getUint8(3);
  if (fps < 1 || fps > 30) return null;
  const count = view.getUint32(4, true);
  if (count > 100_000) return null;
  if (buf.byteLength < 8 + count * 11) return null;
  const intervalMs = Math.round(1000 / fps);
  const out: GhostPoseSample[] = [];
  for (let i = 0; i < count; i++) {
    const off = 8 + i * 11;
    const score = view.getUint32(off, true);
    const xq = view.getUint16(off + 4, true);
    const yq = view.getUint16(off + 6, true);
    const rotq = view.getInt16(off + 8, true);
    const flags = view.getUint8(off + 10);
    out.push({
      t: i * intervalMs,
      score,
      x: xq / POS_SCALE_X,
      y: yq / POS_SCALE_Y,
      rot: rotq / ROT_SCALE,
      flags,
    });
  }
  return out;
}

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function base64ToBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

// ── Publish ──────────────────────────────────────────────────────────────────

export interface PublishGhostInput {
  session: SignetSession;
  /** v1 score-only samples (always present). Used when no pose samples
   *  are supplied — typically on free runs where the pose stream isn't
   *  captured. */
  samples: readonly GhostSample[];
  /** v2 pose samples — present only on daily-mode runs. When supplied,
   *  publishGhost emits a v2 event (pose-bearing) instead of v1. */
  poseSamples?: readonly GhostPoseSample[];
  scoreEventId?: string;
  finalScore: number;
  finalWave: number;
  durationMs: number;
  seed?: string | null;
  cheated?: boolean;
  relays?: readonly string[];
}

/** Sign + broadcast a kind 30763 ghost event. Picks v2 (pose-bearing) when
 *  poseSamples is non-empty, else v1 (score-only). Resolves with the signed
 *  event on success, null when the session can't sign or the run is too
 *  short. Does not throw — relay failures are swallowed since this is
 *  best-effort. */
export async function publishGhost(input: PublishGhostInput): Promise<NostrEvent | null> {
  const { session, samples, poseSamples, scoreEventId, finalScore, finalWave, durationMs, seed, cheated } = input;
  if (!session.signer.capabilities.canSignEvents) return null;
  if (cheated) return null;

  const useV2 = (poseSamples?.length ?? 0) >= 2;
  if (!useV2 && samples.length < 2) return null;

  const dTag = scoreEventId
    ? `${GAME_ID}:${scoreEventId}`
    : `${GAME_ID}:${session.pubkey.slice(0, 16)}:${Date.now()}`;

  const enc = useV2 ? 'ghost-v2' : 'ghost-v1';
  const fps = useV2 ? GHOST_FPS_V2 : GHOST_FPS_V1;
  const content = useV2
    ? encodeGhostV2(poseSamples!)
    : encodeGhostV1(samples);

  const tags: string[][] = [
    ['d', dTag],
    ['t', 'pallasite'],
    ['game', GAME_ID],
    ['score', finalScore.toString()],
    ['wave', finalWave.toString()],
    ['duration', durationMs.toString()],
    ['enc', enc],
    ['fps', fps.toString()],
  ];
  if (scoreEventId) tags.push(['e', scoreEventId]);
  if (seed) tags.push(['seed', seed]);

  const signed = await session.signer.signEvent({
    kind: GHOST_KIND,
    content,
    tags,
  });

  const relays = ghostRelaySet(input.relays);
  await Promise.all(relays.map(url => publishToRelay(url, signed).catch(() => undefined)));
  return signed;
}

// ── Full-world replay (kind 30764) ──────────────────────────────────────────

import type { ReplayFrameRaw } from './stream-session.js';

export interface PublishReplayInput {
  session: SignetSession;
  /** Optional kind 30762 score event id. When supplied, the kind 30764
   *  carries an `e` tag pointing at it so watchers can fetch the
   *  replay by score id (same pattern as kind 30763 ghosts). When not
   *  supplied (player hasn't claimed yet, or claim raced this call),
   *  the replay still publishes with a timestamp-based d-tag and is
   *  findable via author + since. */
  scoreEventId?: string;
  finalScore: number;
  finalWave: number;
  durationMs: number;
  frames: ReadonlyArray<ReplayFrameRaw>;
  relays?: readonly string[];
  /** Optional pre-gzipped frames bytes. When the caller is retrying a
   *  previously-built replay (signer hiccup, RETRY button), reusing
   *  the gzipped bytes skips re-compressing 14k frames on each click
   *  — gzip a 3MB replay is ~500ms of CPU on a phone. The caller is
   *  responsible for ensuring these bytes are gzip(JSON.stringify(
   *  frames)) for the same frame array. */
  gzippedFrames?: Uint8Array;
}

/** Gzip a frame array and return the bytes. Exported so the game-over
 *  retry UI can compress once at run end and cache the result for
 *  subsequent retries without recompressing every click. Returns null
 *  on CompressionStream failure (very rare — graceful fallback path
 *  is to skip the replay entirely). */
export async function gzipReplayFrames(frames: ReadonlyArray<ReplayFrameRaw>): Promise<Uint8Array | null> {
  return gzipFrames(frames);
}

/** Gzip a JSON-serialisable frame array to raw bytes. The Blossom-style
 *  upload path PUTs these bytes directly to /api/replay/{sha256} — no
 *  base64 envelope, no chunking. */
async function gzipFrames(frames: ReadonlyArray<ReplayFrameRaw>): Promise<Uint8Array | null> {
  try {
    const json = JSON.stringify(frames);
    const blob = new Blob([json]);
    const cs = new (window as unknown as { CompressionStream: new (fmt: string) => GenericTransformStream }).CompressionStream('gzip');
    const compressed = await new Response(blob.stream().pipeThrough(cs)).blob();
    const buf = await compressed.arrayBuffer();
    return new Uint8Array(buf);
  } catch {
    return null;
  }
}

/** Reverse of gzipFrames — gunzip a Uint8Array back into ReplayFrameRaw[]. */
async function gunzipFrames(bytes: Uint8Array): Promise<ReplayFrameRaw[] | null> {
  try {
    const ab = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(ab).set(bytes);
    const blob = new Blob([ab]);
    const ds = new (window as unknown as { DecompressionStream: new (fmt: string) => GenericTransformStream }).DecompressionStream('gzip');
    const text = await new Response(blob.stream().pipeThrough(ds)).text();
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return null;
    return parsed as ReplayFrameRaw[];
  } catch {
    return null;
  }
}

/** Publish a full-run replay: gzip every frame into a single blob,
 *  upload to /api/replay/{sha256} via NIP-98, then sign ONE kind 30764
 *  pointer event tagging the URL + hash. Replaces the previous per-wave
 *  chunked publish path — that pattern required 25+ sequential signs
 *  per run and was the proximate cause of bark-extension overload
 *  blocking claims and ghost publishes.
 *
 *  Returns the pointer event on success. Null on upload failure,
 *  signer failure, or relay-publish failure. The pointer event's id
 *  is the shareable replay link.
 *
 *  Sign count for the entire replay flow: 2 (the NIP-98 auth for the
 *  upload + the pointer event). Prior path: 1 + N waves. */
export async function publishReplay(input: PublishReplayInput): Promise<NostrEvent | null> {
  const { session, scoreEventId, finalScore, finalWave, durationMs, frames, gzippedFrames } = input;
  if (!session.signer.capabilities.canSignEvents) {
    console.warn('[replay] skip publish — signer cannot sign events');
    return null;
  }
  if (frames.length < 2) {
    console.warn(`[replay] skip publish — only ${frames.length} frame(s) buffered (need >=2)`);
    return null;
  }

  // Reuse pre-gzipped bytes when the caller provided them (RETRY path)
  // to avoid re-compressing 14k frames on every click. First-time
  // publish path still re-gzips inline.
  const gzipped = gzippedFrames ?? await gzipFrames(frames);
  if (!gzipped) {
    console.error('[replay] gzip failed');
    return null;
  }
  if (!gzippedFrames) {
    console.log(`[replay] gzipped ${frames.length} frames → ${gzipped.byteLength}B`);
  }

  const uploaded = await uploadReplay(session, gzipped);
  if (!uploaded.ok) {
    const detail = 'detail' in uploaded ? uploaded.detail ?? '' : '';
    console.error(`[replay] upload failed: ${uploaded.error}${detail ? ` (${detail})` : ''}`);
    return null;
  }
  console.log(`[replay] uploaded ${uploaded.size}B blob, sha256=${uploaded.sha256.slice(0, 8)}…`);

  const runId = `${session.pubkey.slice(0, 16)}:${Date.now()}`;
  const relays = ghostRelaySet(input.relays);

  // Pointer event — content stays empty; the bytes live at the URL.
  // `enc=replay-blob-v1` tells the watcher to follow `url` rather than
  // try to decode content directly. `x` carries the sha256 so the
  // watcher can verify the bytes after fetching (defence against
  // server compromise or transit corruption).
  const tags: string[][] = [
    ['d', `${GAME_ID}:replay:${runId}`],
    ['t', 'pallasite'],
    ['game', GAME_ID],
    ['enc', 'replay-blob-v1'],
    ['url', uploaded.url],
    ['x', uploaded.sha256],
    ['size', String(uploaded.size)],
    ['score', finalScore.toString()],
    ['wave', finalWave.toString()],
    ['duration', durationMs.toString()],
    ['frames', String(frames.length)],
    ['runid', runId],
  ];
  if (scoreEventId) tags.push(['e', scoreEventId, '', 'score']);

  let signed: NostrEvent;
  try {
    signed = await session.signer.signEvent({
      kind: REPLAY_KIND,
      content: '',
      tags,
    });
  } catch (err) {
    console.error(`[replay] pointer-event sign failed: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
  const results = await Promise.allSettled(relays.map((u) => publishToRelay(u, signed)));
  const ok = results.filter((r) => r.status === 'fulfilled').length;
  for (let i = 0; i < relays.length; i++) {
    const r = results[i];
    if (r.status === 'fulfilled') console.log(`[replay]   ✓ ${relays[i]}`);
    else console.warn(`[replay]   ✗ ${relays[i]} — ${(r.reason as Error)?.message ?? 'error'}`);
  }
  console.log(`[replay] pointer ${signed.id.slice(0, 8)}… published to ${ok}/${relays.length} relays`);
  return signed;
}

/** Fetch + verify + decompress the blob at the pointer event's `url`.
 *  Returns null if the URL is unreachable or the sha256 doesn't match
 *  the `x` tag.
 *
 *  Server sends raw gzipped bytes as Content-Type: application/gzip
 *  (no Content-Encoding header) so the browser doesn't auto-decompress
 *  — we need to see the raw bytes to hash them. We hash, verify against
 *  the pointer event's commitment, then gunzip in JS. */
async function fetchBlobReplay(url: string, expectedSha256: string): Promise<ReplayFrameRaw[] | null> {
  try {
    const res = await fetch(url, { cache: 'force-cache' });
    if (!res.ok) {
      console.warn(`[replay] blob fetch failed HTTP ${res.status}: ${url}`);
      return null;
    }
    const ab = await res.arrayBuffer();
    const bytes = new Uint8Array(ab);
    const actualSha = await sha256HexUint8(bytes);
    if (actualSha !== expectedSha256) {
      console.warn(`[replay] sha256 mismatch — expected ${expectedSha256.slice(0, 8)}… got ${actualSha.slice(0, 8)}… — refusing`);
      return null;
    }
    return await gunzipFrames(bytes);
  } catch (err) {
    console.warn(`[replay] blob fetch threw: ${err instanceof Error ? err.message : String(err)}`);
    return null;
  }
}

async function sha256HexUint8(bytes: Uint8Array): Promise<string> {
  const ab = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(ab).set(bytes);
  const hash = await crypto.subtle.digest('SHA-256', ab);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Find the most recent kind 30764 replay from `pubkey`. Used by the
 *  live theatre's "WATCH FROM START" button when the spectator doesn't
 *  know the score event id yet (e.g. player has just claimed and the
 *  faucet's kind 30762 hasn't propagated to relays). */
export async function findReplayByAuthor(
  pubkey: string,
  sinceSec: number,
  opts: { relays?: readonly string[] } = {},
): Promise<FetchedReplay | null> {
  if (!/^[0-9a-f]{64}$/i.test(pubkey)) return null;
  const relays = ghostRelaySet(opts.relays);
  if (relays.length === 0) return null;
  console.log(`[replay] findReplayByAuthor pubkey=${pubkey.slice(0, 8)}… since=${sinceSec} relays=[${relays.join(', ')}]`);

  const filter: Record<string, unknown> = {
    kinds: [REPLAY_KIND],
    authors: [pubkey],
    since: Math.max(0, sinceSec - 30),
    limit: 5,
  };
  const perRelay = new Map<string, number>();
  const events = await new Promise<NostrEvent[]>((resolve) => {
    const collected: NostrEvent[] = [];
    let done = 0;
    const sockets: WebSocket[] = [];
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sockets.forEach((s) => { try { s.close(); } catch { /* ignore */ } });
      resolve(collected);
    };
    const timer = setTimeout(settle, FETCH_TIMEOUT_MS);
    const markDone = (): void => { done += 1; if (done >= relays.length) settle(); };
    for (const url of relays) {
      let ws: WebSocket;
      try { ws = new WebSocket(url); perRelay.set(url, 0); } catch { console.warn(`[replay]   ✗ ${url} — ws ctor threw`); markDone(); continue; }
      sockets.push(ws);
      const subId = 'rb' + Math.random().toString(36).slice(2, 10);
      ws.onopen = () => ws.send(JSON.stringify(['REQ', subId, filter]));
      ws.onmessage = (ev) => {
        try {
          const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
          if (!Array.isArray(msg)) return;
          if (msg[0] === 'EVENT' && msg[1] === subId) {
            const e = msg[2];
            if (e && typeof e === 'object' && (e as { kind?: number }).kind === REPLAY_KIND) {
              collected.push(e as NostrEvent);
              perRelay.set(url, (perRelay.get(url) ?? 0) + 1);
            }
          } else if (msg[0] === 'EOSE' && msg[1] === subId) {
            markDone();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => { console.warn(`[replay]   ✗ ${url} — ws error`); markDone(); };
      ws.onclose = markDone;
    }
  });
  for (const [url, count] of perRelay) {
    console.log(`[replay]   ${count > 0 ? '✓' : '·'} ${url} returned ${count} event(s)`);
  }
  events.sort((a, b) => b.created_at - a.created_at);
  console.log(`[replay] findReplayByAuthor got ${events.length} candidate event(s)`);

  // ── Blossom blob path (current publish format) ──────────────────
  // A blob-pointer event is `enc=replay-blob-v1` with a `url` + `x`
  // tag. Fetch the URL, verify sha256, gunzip, return frames.
  for (const e of events) {
    if (tagValue(e.tags, 'enc') !== 'replay-blob-v1') continue;
    if (!hasTagValue(e.tags, 'game', GAME_ID)) continue;
    const url = tagValue(e.tags, 'url');
    const sha = tagValue(e.tags, 'x');
    if (!url || !sha) continue;
    console.log(`[replay] blob pointer ${e.id.slice(0, 8)}… → ${url.slice(0, 50)}…`);
    const frames = await fetchBlobReplay(url, sha);
    if (frames && frames.length >= 2) {
      return {
        eventId: e.id,
        pubkey: e.pubkey,
        score: parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0,
        wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
        durationMs: parseInt(tagValue(e.tags, 'duration') ?? '0', 10) || 0,
        frames,
      };
    }
    console.warn('[replay] blob fetch failed — falling back to legacy paths');
  }

  // ── Legacy manifest path (per-wave chunked publish format) ───────
  // A manifest is a kind 30764 event with a `chunk` tag value of 'm'.
  // Its content is empty; its e-tags list the chunk events (one per
  // wave). Kept for backwards compatibility with replays already
  // published before the Blossom migration.
  const manifests = events.filter((e) =>
    hasTagValue(e.tags, 'game', GAME_ID) && tagValue(e.tags, 'chunk') === 'm'
  );
  if (manifests.length > 0) {
    const manifest = manifests[0];
    console.log(`[replay] manifest ${manifest.id.slice(0, 8)}… found — fetching chunks`);
    const frames = await fetchChunksForManifest(manifest, relays);
    if (frames && frames.length >= 2) {
      console.log(`[replay] concatenated ${frames.length} frames from ${manifests[0].tags.filter((t) => t[0] === 'e' && (t[3] ?? '').startsWith('wave-')).length} wave chunk(s)`);
      return {
        eventId: manifest.id,
        pubkey: manifest.pubkey,
        score: parseInt(tagValue(manifest.tags, 'score') ?? '0', 10) || 0,
        wave: parseInt(tagValue(manifest.tags, 'wave') ?? '0', 10) || 0,
        durationMs: parseInt(tagValue(manifest.tags, 'duration') ?? '0', 10) || 0,
        frames,
      };
    }
    console.warn('[replay] manifest found but chunks couldn\'t be assembled — falling back to legacy single-event scan');
  }

  // ── Legacy single-event path ────────────────────────────────────
  // Pre-chunked replays (or single-event runs that happened to fit
  // in 64KB) have no `chunk` tag and the entire content is a single
  // gzip+base64 blob. Decode directly.
  for (const e of events) {
    const gameTag = tagValue(e.tags, 'game');
    const encTag = tagValue(e.tags, 'enc');
    const chunkTag = tagValue(e.tags, 'chunk');
    if (chunkTag !== undefined) continue;  // chunked replay; manifest path handles these
    if (!hasTagValue(e.tags, 'game', GAME_ID)) {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — game tag is "${gameTag ?? '<none>'}" not "${GAME_ID}"`);
      continue;
    }
    if (encTag !== 'replay-gzip-b64') {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — enc tag is "${encTag ?? '<none>'}" not "replay-gzip-b64"`);
      continue;
    }
    const frames = await decodeReplay(e.content);
    if (!frames) {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — gzip+base64 decode failed (content length ${e.content.length})`);
      continue;
    }
    console.log(`[replay] decoded ${frames.length} frames from legacy single event ${e.id.slice(0, 8)}…`);
    return {
      eventId: e.id,
      pubkey: e.pubkey,
      score: parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0,
      wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
      durationMs: parseInt(tagValue(e.tags, 'duration') ?? '0', 10) || 0,
      frames,
    };
  }
  if (events.length === 0) {
    console.warn(
      `[replay] no kind ${REPLAY_KIND} events on any relay for author ${pubkey.slice(0, 8)}…\n` +
      `  → publish path never fired, OR all relays in the queried set ` +
      `rejected/dropped the event. If you just played a run as ${pubkey.slice(0, 8)}…, ` +
      `look for a '[replay] manifest ... published' line in your PLAYER's console.`,
    );
  } else {
    console.warn(`[replay] ${events.length} kind ${REPLAY_KIND} events fetched but none usable — see per-event skip warnings above`);
  }
  return null;
}

/** Fetch a manifest's chunk events in parallel and assemble the full
 *  frame timeline. Each chunk is a kind 30764 event identified by an
 *  e-tag on the manifest with role marker `wave-<N>`. Returns null if
 *  no chunks could be loaded; partial chunks are tolerated (missing
 *  ones just leave gaps). */
async function fetchChunksForManifest(
  manifest: NostrEvent,
  relays: readonly string[],
): Promise<ReplayFrameRaw[] | null> {
  type ChunkRef = { wave: number; eventId: string };
  const chunkRefs: ChunkRef[] = [];
  for (const t of manifest.tags) {
    if (t[0] !== 'e' || typeof t[1] !== 'string') continue;
    const marker = t[3] ?? '';
    if (!marker.startsWith('wave-')) continue;  // skip the score-event 'e' tag and unknowns
    const wave = parseInt(marker.slice(5), 10);
    if (!Number.isFinite(wave)) continue;
    if (!/^[0-9a-f]{64}$/i.test(t[1])) continue;
    chunkRefs.push({ wave, eventId: t[1] });
  }
  if (chunkRefs.length === 0) {
    console.warn('[replay] manifest had no wave-N e-tags — cannot reassemble');
    return null;
  }
  chunkRefs.sort((a, b) => a.wave - b.wave);
  console.log(`[replay] fetching ${chunkRefs.length} wave chunk(s) for manifest ${manifest.id.slice(0, 8)}…`);

  // Single bulk filter — one REQ across all chunk ids per relay,
  // which is far cheaper than N parallel REQs and most relays handle
  // a 25-id filter inside their limit policy.
  const filter: Record<string, unknown> = {
    kinds: [REPLAY_KIND],
    ids: chunkRefs.map((c) => c.eventId),
    limit: chunkRefs.length,
  };
  const events = await new Promise<NostrEvent[]>((resolve) => {
    const collected: NostrEvent[] = [];
    let done = 0;
    const sockets: WebSocket[] = [];
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sockets.forEach((s) => { try { s.close(); } catch { /* ignore */ } });
      resolve(collected);
    };
    const timer = setTimeout(settle, FETCH_TIMEOUT_MS);
    const markDone = (): void => { done += 1; if (done >= relays.length) settle(); };
    for (const url of relays) {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { markDone(); continue; }
      sockets.push(ws);
      const subId = 'mc' + Math.random().toString(36).slice(2, 10);
      ws.onopen = () => ws.send(JSON.stringify(['REQ', subId, filter]));
      ws.onmessage = (ev) => {
        try {
          const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
          if (!Array.isArray(msg)) return;
          if (msg[0] === 'EVENT' && msg[1] === subId) {
            const e = msg[2];
            if (e && typeof e === 'object' && (e as { kind?: number }).kind === REPLAY_KIND) {
              collected.push(e as NostrEvent);
            }
          } else if (msg[0] === 'EOSE' && msg[1] === subId) {
            markDone();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = markDone;
      ws.onclose = markDone;
    }
  });
  // De-dupe by id (multiple relays may return the same event).
  const byId = new Map<string, NostrEvent>();
  for (const e of events) byId.set(e.id, e);
  // Decode in wave order so concat produces a contiguous timeline.
  const allFrames: ReplayFrameRaw[] = [];
  let decoded = 0;
  for (const ref of chunkRefs) {
    const e = byId.get(ref.eventId);
    if (!e) {
      console.warn(`[replay]   wave ${ref.wave} chunk ${ref.eventId.slice(0, 8)}… not on any relay — skipping`);
      continue;
    }
    if (tagValue(e.tags, 'enc') !== 'replay-gzip-b64') continue;
    const f = await decodeReplay(e.content);
    if (!f) {
      console.warn(`[replay]   wave ${ref.wave} chunk decode failed`);
      continue;
    }
    decoded += 1;
    for (const fr of f) allFrames.push(fr);
  }
  if (decoded === 0) {
    console.warn('[replay] no chunks decoded — assembly failed');
    return null;
  }
  console.log(`[replay] assembled ${allFrames.length} frames from ${decoded}/${chunkRefs.length} chunks`);
  return allFrames;
}

export interface FetchedReplay {
  eventId: string;
  pubkey: string;
  score: number;
  wave: number;
  durationMs: number;
  frames: ReplayFrameRaw[];
}

/** Fetch + decode the kind 30764 replay for the supplied score event
 *  id. Returns null if the relay set doesn't have one yet (player has
 *  not claimed) or decompression fails. */
/** Fetch + decode the kind 30764 replay by its own event id. Used by
 *  the share-link flow — a watcher receives a URL like
 *  `watch.pallasite.app/#replay=<event-id>` and opens directly into
 *  the rich replay theatre. Resolves null on relay timeout or decode
 *  failure. */
export async function fetchReplayByEventId(
  eventId: string,
  opts: { relays?: readonly string[] } = {},
): Promise<FetchedReplay | null> {
  if (!/^[0-9a-f]{64}$/i.test(eventId)) return null;
  const relays = ghostRelaySet(opts.relays);
  if (relays.length === 0) return null;
  console.log(`[replay] fetchReplayByEventId ${eventId.slice(0, 8)}… relays=${relays.length}`);

  const filter: Record<string, unknown> = {
    kinds: [REPLAY_KIND],
    ids: [eventId],
    limit: 1,
  };

  const events = await new Promise<NostrEvent[]>((resolve) => {
    const collected: NostrEvent[] = [];
    let done = 0;
    const sockets: WebSocket[] = [];
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sockets.forEach((s) => { try { s.close(); } catch { /* ignore */ } });
      resolve(collected);
    };
    const timer = setTimeout(settle, FETCH_TIMEOUT_MS);
    const markDone = (): void => { done += 1; if (done >= relays.length) settle(); };
    for (const url of relays) {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { markDone(); continue; }
      sockets.push(ws);
      const subId = 're' + Math.random().toString(36).slice(2, 10);
      ws.onopen = () => ws.send(JSON.stringify(['REQ', subId, filter]));
      ws.onmessage = (ev) => {
        try {
          const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
          if (!Array.isArray(msg)) return;
          if (msg[0] === 'EVENT' && msg[1] === subId) {
            const e = msg[2];
            if (e && typeof e === 'object' && (e as { kind?: number }).kind === REPLAY_KIND) {
              collected.push(e as NostrEvent);
            }
          } else if (msg[0] === 'EOSE' && msg[1] === subId) {
            markDone();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = markDone;
      ws.onclose = markDone;
    }
  });

  console.log(`[replay] fetchReplayByEventId got ${events.length} candidate event(s)`);
  for (const e of events) {
    const gameTag = tagValue(e.tags, 'game');
    const encTag = tagValue(e.tags, 'enc');
    if (!hasTagValue(e.tags, 'game', GAME_ID)) {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — game tag is "${gameTag ?? '<none>'}" not "${GAME_ID}"`);
      continue;
    }
    // New Blossom-blob format: follow the URL + verify hash.
    if (encTag === 'replay-blob-v1') {
      const url = tagValue(e.tags, 'url');
      const sha = tagValue(e.tags, 'x');
      if (!url || !sha) {
        console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — replay-blob-v1 missing url/x tag`);
        continue;
      }
      const frames = await fetchBlobReplay(url, sha);
      if (!frames) continue;
      return {
        eventId: e.id,
        pubkey: e.pubkey,
        score: parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0,
        wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
        durationMs: parseInt(tagValue(e.tags, 'duration') ?? '0', 10) || 0,
        frames,
      };
    }
    // Legacy single-event format kept for older replays still on relays.
    if (encTag !== 'replay-gzip-b64') {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — enc tag is "${encTag ?? '<none>'}" (need replay-blob-v1 or replay-gzip-b64)`);
      continue;
    }
    const frames = await decodeReplay(e.content);
    if (!frames) {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — gzip+base64 decode failed`);
      continue;
    }
    return {
      eventId: e.id,
      pubkey: e.pubkey,
      score: parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0,
      wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
      durationMs: parseInt(tagValue(e.tags, 'duration') ?? '0', 10) || 0,
      frames,
    };
  }
  if (events.length === 0) console.warn(`[replay] no kind ${REPLAY_KIND} on relays for event id ${eventId.slice(0, 8)}…`);
  return null;
}

export async function fetchReplayByScoreEventId(
  scoreEventId: string,
  opts: { relays?: readonly string[] } = {},
): Promise<FetchedReplay | null> {
  if (!/^[0-9a-f]{64}$/i.test(scoreEventId)) return null;
  const relays = ghostRelaySet(opts.relays);
  if (relays.length === 0) return null;

  const filter: Record<string, unknown> = {
    kinds: [REPLAY_KIND],
    '#e': [scoreEventId],
    '#t': ['pallasite'],
    limit: 3,
  };

  const events = await new Promise<NostrEvent[]>((resolve) => {
    const collected: NostrEvent[] = [];
    let done = 0;
    const sockets: WebSocket[] = [];
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sockets.forEach((s) => { try { s.close(); } catch { /* ignore */ } });
      resolve(collected);
    };
    const timer = setTimeout(settle, FETCH_TIMEOUT_MS);
    const markDone = (): void => {
      done += 1;
      if (done >= relays.length) settle();
    };
    for (const url of relays) {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { markDone(); continue; }
      sockets.push(ws);
      const subId = 'rp' + Math.random().toString(36).slice(2, 10);
      ws.onopen = () => ws.send(JSON.stringify(['REQ', subId, filter]));
      ws.onmessage = (ev) => {
        try {
          const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
          if (!Array.isArray(msg)) return;
          if (msg[0] === 'EVENT' && msg[1] === subId) {
            const e = msg[2];
            if (e && typeof e === 'object' && (e as { kind?: number }).kind === REPLAY_KIND) {
              collected.push(e as NostrEvent);
            }
          } else if (msg[0] === 'EOSE' && msg[1] === subId) {
            markDone();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = markDone;
      ws.onclose = markDone;
    }
  });

  console.log(`[replay] fetchReplayByScoreEventId got ${events.length} candidate event(s)`);
  for (const e of events) {
    const gameTag = tagValue(e.tags, 'game');
    const encTag = tagValue(e.tags, 'enc');
    if (!hasTagValue(e.tags, 'game', GAME_ID)) {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — game tag "${gameTag ?? '<none>'}"`);
      continue;
    }
    if (encTag === 'replay-blob-v1') {
      const url = tagValue(e.tags, 'url');
      const sha = tagValue(e.tags, 'x');
      if (!url || !sha) {
        console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — replay-blob-v1 missing url/x tag`);
        continue;
      }
      const frames = await fetchBlobReplay(url, sha);
      if (!frames) continue;
      return {
        eventId: e.id,
        pubkey: e.pubkey,
        score: parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0,
        wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
        durationMs: parseInt(tagValue(e.tags, 'duration') ?? '0', 10) || 0,
        frames,
      };
    }
    if (encTag !== 'replay-gzip-b64') {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — enc "${encTag ?? '<none>'}"`);
      continue;
    }
    const frames = await decodeReplay(e.content);
    if (!frames) {
      console.warn(`[replay]   skip ${e.id.slice(0, 8)}… — decode failed`);
      continue;
    }
    return {
      eventId: e.id,
      pubkey: e.pubkey,
      score: parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0,
      wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
      durationMs: parseInt(tagValue(e.tags, 'duration') ?? '0', 10) || 0,
      frames,
    };
  }
  if (events.length === 0) {
    console.warn(`[replay] no kind ${REPLAY_KIND} #e-tagged to ${scoreEventId.slice(0, 8)}… on any relay`);
  }
  return null;
}

async function decodeReplay(b64: string): Promise<ReplayFrameRaw[] | null> {
  try {
    const bytes = base64ToBytes(b64);
    // Slice into a fresh ArrayBuffer to dodge SharedArrayBuffer typing
    // friction in strict TS configs (Uint8Array.buffer may report a
    // SharedArrayBuffer in some lib configurations).
    const ab = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(ab).set(bytes);
    const blob = new Blob([ab]);
    const ds = new (window as unknown as { DecompressionStream: new (fmt: string) => GenericTransformStream }).DecompressionStream('gzip');
    const text = await new Response(blob.stream().pipeThrough(ds)).text();
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return null;
    return parsed as ReplayFrameRaw[];
  } catch {
    return null;
  }
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function publishToRelay(url: string, event: NostrEvent, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try { ws = new WebSocket(url); } catch (err) { reject(err); return; }
    const timer = setTimeout(() => {
      try { ws.close(); } catch { /* ignore */ }
      reject(new Error('relay-timeout'));
    }, timeoutMs);
    ws.onopen = () => ws.send(JSON.stringify(['EVENT', event]));
    ws.onmessage = ev => {
      try {
        const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        if (Array.isArray(msg) && msg[0] === 'OK' && msg[1] === event.id) {
          clearTimeout(timer);
          try { ws.close(); } catch { /* ignore */ }
          if (msg[2] === true) resolve();
          else reject(new Error(typeof msg[3] === 'string' ? msg[3] : 'rejected'));
        }
      } catch { /* ignore */ }
    };
    ws.onerror = () => { clearTimeout(timer); reject(new Error('relay-error')); };
  });
}

// ── Fetch ────────────────────────────────────────────────────────────────────

const FETCH_TIMEOUT_MS = 5000;
const TOP_GHOST_CACHE_TTL_MS = 60_000;
/** Cache keyed by seed (or '' for the seedless global top). Lets the title
 *  screen prefetch both the daily ghost and the global ghost without one
 *  overwriting the other; render.ts can then prefer the daily entry and
 *  fall back to the global one for the attract loop. */
const topGhostCache = new Map<string, { at: number; ghost: GhostRun | null }>();

/** Pull the highest-score kind 30763 event for this game. Optionally filtered
 *  by daily seed (so daily mode races a same-seed ghost). Cached for 60s per
 *  seed. Resolves null on relay failure rather than throwing. */
export async function fetchTopGhost(
  opts: { seed?: string | null; relays?: readonly string[]; force?: boolean } = {},
): Promise<GhostRun | null> {
  const cacheKey = opts.seed ?? '';
  if (!opts.force) {
    const hit = topGhostCache.get(cacheKey);
    if (hit && Date.now() - hit.at < TOP_GHOST_CACHE_TTL_MS) return hit.ghost;
  }
  const relays = ghostRelaySet(opts.relays);
  if (relays.length === 0) return null;

  const filter: Record<string, unknown> = {
    kinds: [GHOST_KIND],
    '#t': ['pallasite'],
    limit: 100,
  };
  if (opts.seed) (filter as Record<string, string[]>)['#seed'] = [opts.seed];

  const events = await new Promise<NostrEvent[]>(resolve => {
    const collected: NostrEvent[] = [];
    let done = 0;
    const sockets: WebSocket[] = [];
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sockets.forEach(s => { try { s.close(); } catch { /* ignore */ } });
      resolve(collected);
    };
    const timer = setTimeout(settle, FETCH_TIMEOUT_MS);
    const markDone = (): void => {
      done += 1;
      if (done >= relays.length) settle();
    };
    for (const url of relays) {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { markDone(); continue; }
      sockets.push(ws);
      const subId = 'gh' + Math.random().toString(36).slice(2, 10);
      ws.onopen = () => ws.send(JSON.stringify(['REQ', subId, filter]));
      ws.onmessage = ev => {
        try {
          const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
          if (!Array.isArray(msg)) return;
          if (msg[0] === 'EVENT' && msg[1] === subId) {
            const e = msg[2];
            if (isGhostEvent(e)) collected.push(e);
          } else if (msg[0] === 'EOSE' && msg[1] === subId) {
            markDone();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = markDone;
      ws.onclose = markDone;
    }
  });

  let best: GhostRun | null = null;
  for (const e of events) {
    if (!hasTagValue(e.tags, 'game', GAME_ID)) continue;
    const enc = tagValue(e.tags, 'enc');
    if (enc !== 'ghost-v1' && enc !== 'ghost-v2') continue;
    const score = parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0;
    if (score <= 0) continue;
    if (best && best.score >= score) continue;

    let samples: GhostSample[];
    let poseSamples: GhostPoseSample[] | undefined;
    let fps: number;
    if (enc === 'ghost-v2') {
      const pose = decodeGhostV2(e.content);
      if (!pose || pose.length < 2) continue;
      poseSamples = pose;
      fps = GHOST_FPS_V2;
      // Derive 1Hz score samples from the pose stream by picking every Nth
      // frame so the leader chip can read scoreSamples without a separate
      // decode path. Fallback to per-frame if fps is below 1Hz somehow.
      const stride = Math.max(1, Math.floor(fps / GHOST_FPS_V1));
      samples = [];
      for (let i = 0; i < pose.length; i += stride) {
        samples.push({ t: pose[i].t, score: pose[i].score });
      }
    } else {
      const v1 = decodeGhostV1(e.content);
      if (!v1 || v1.length < 2) continue;
      samples = v1;
      fps = GHOST_FPS_V1;
    }

    const declaredDuration = parseInt(tagValue(e.tags, 'duration') ?? '0', 10);
    const durationMs = declaredDuration > 0
      ? declaredDuration
      : (poseSamples ?? samples)[(poseSamples ?? samples).length - 1].t;
    best = {
      pubkey: e.pubkey,
      score,
      wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
      seed: tagValue(e.tags, 'seed'),
      encoding: enc,
      fps,
      samples,
      poseSamples,
      durationMs,
      eventId: e.id,
    };
  }

  topGhostCache.set(cacheKey, { at: Date.now(), ghost: best });
  return best;
}

/** Synchronous read of the cached top ghost — no network. Used by render.ts
 *  to draw the leader chip without bouncing through async on every frame. */
export function getCachedGhost(seed?: string | null): GhostRun | null {
  const wantKey = seed ?? '';
  const hit = topGhostCache.get(wantKey);
  return hit?.ghost ?? null;
}

/** Title-screen warm-up read cap. Ghosts are published to EVERY active relay
 *  (see publishGhost), so the global-best ghost is replicated across all of
 *  them — reading the first few primary relays finds the same winner as fanning
 *  out to all ~8, without the title opening a socket to every relay at once
 *  (which reads as beacon-like to network-reputation scanners). Forced /
 *  explicit fetches still use the full set. */
const PREFETCH_RELAY_CAP = 3;

/** Fire-and-forget prefetch for the title screen. Errors swallowed. When a
 *  seed is supplied, also warms the global-top fetch so the attract-loop has a
 *  fallback to render even if no ghost has been published for today's seed yet.
 *
 *  Reads a capped primary subset and SEQUENCES the seed + global fetches rather
 *  than firing both across every relay simultaneously, so the title doesn't open
 *  a fan-out of sockets on load. See PREFETCH_RELAY_CAP. */
export function prefetchTopGhost(seed?: string | null): void {
  const relays = ghostRelaySet().slice(0, PREFETCH_RELAY_CAP);
  void (async () => {
    try {
      await fetchTopGhost({ seed, relays });
      // Only warm the global fallback if the daily seed didn't already cache a
      // ghost, and only after the seed fetch settles — never alongside it.
      if (seed && !getCachedGhost(null)) {
        await fetchTopGhost({ seed: null, relays });
      }
    } catch { /* ignore */ }
  })();
}

/**
 * Fetch the ghost (kind 30763) that references a specific kind 30762 score
 * event. Used by the replay theatre to play back a leaderboard entry: the
 * leaderboard row knows its own score event id, and ghost events tag the
 * score event via `e`, so the lookup is a direct relay query.
 *
 * Resolves null on relay timeout, decode failure, or absent ghost (not all
 * scores have a published replay). Does not throw -- the leaderboard caller
 * shows a "no replay available" state on null instead of erroring.
 */
export async function fetchGhostByScoreEventId(
  scoreEventId: string,
  opts: { relays?: readonly string[] } = {},
): Promise<GhostRun | null> {
  if (!/^[0-9a-f]{64}$/i.test(scoreEventId)) return null;
  const relays = ghostRelaySet(opts.relays);
  if (relays.length === 0) return null;

  const filter: Record<string, unknown> = {
    kinds: [GHOST_KIND],
    '#e': [scoreEventId],
    '#t': ['pallasite'],
    limit: 5,
  };

  const events = await new Promise<NostrEvent[]>(resolve => {
    const collected: NostrEvent[] = [];
    let done = 0;
    const sockets: WebSocket[] = [];
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sockets.forEach(s => { try { s.close(); } catch { /* ignore */ } });
      resolve(collected);
    };
    const timer = setTimeout(settle, FETCH_TIMEOUT_MS);
    const markDone = (): void => {
      done += 1;
      if (done >= relays.length) settle();
    };
    for (const url of relays) {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { markDone(); continue; }
      sockets.push(ws);
      const subId = 'gx' + Math.random().toString(36).slice(2, 10);
      ws.onopen = () => ws.send(JSON.stringify(['REQ', subId, filter]));
      ws.onmessage = ev => {
        try {
          const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
          if (!Array.isArray(msg)) return;
          if (msg[0] === 'EVENT' && msg[1] === subId) {
            const e = msg[2];
            if (isGhostEvent(e)) collected.push(e);
          } else if (msg[0] === 'EOSE' && msg[1] === subId) {
            markDone();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = markDone;
      ws.onclose = markDone;
    }
  });

  for (const e of events) {
    if (!hasTagValue(e.tags, 'game', GAME_ID)) continue;
    const enc = tagValue(e.tags, 'enc');
    if (enc !== 'ghost-v1' && enc !== 'ghost-v2') continue;
    const score = parseInt(tagValue(e.tags, 'score') ?? '0', 10) || 0;
    if (score <= 0) continue;

    let samples: GhostSample[];
    let poseSamples: GhostPoseSample[] | undefined;
    let fps: number;
    if (enc === 'ghost-v2') {
      const pose = decodeGhostV2(e.content);
      if (!pose || pose.length < 2) continue;
      poseSamples = pose;
      fps = GHOST_FPS_V2;
      const stride = Math.max(1, Math.floor(fps / GHOST_FPS_V1));
      samples = [];
      for (let i = 0; i < pose.length; i += stride) {
        samples.push({ t: pose[i].t, score: pose[i].score });
      }
    } else {
      const v1 = decodeGhostV1(e.content);
      if (!v1 || v1.length < 2) continue;
      samples = v1;
      fps = GHOST_FPS_V1;
    }

    const declaredDuration = parseInt(tagValue(e.tags, 'duration') ?? '0', 10);
    const durationMs = declaredDuration > 0
      ? declaredDuration
      : (poseSamples ?? samples)[(poseSamples ?? samples).length - 1].t;

    return {
      pubkey: e.pubkey,
      score,
      wave: parseInt(tagValue(e.tags, 'wave') ?? '0', 10) || 0,
      seed: tagValue(e.tags, 'seed'),
      encoding: enc,
      fps,
      samples,
      poseSamples,
      durationMs,
      eventId: e.id,
    };
  }
  return null;
}

/**
 * Find the most recent ghost from `pubkey` created after `sinceSec` and
 * return its score-event-id (the 'e' tag pointing at the kind 30762 final
 * score event). Useful when a spectator wants to replay a run they just
 * watched live — the live theatre knows the player pubkey and run start
 * time but not the score event id directly. Returns null if no ghost has
 * been published yet (player hasn't claimed) or no relays respond.
 */
export async function findScoreIdForLatestGhost(
  pubkey: string,
  sinceSec: number,
  opts: { relays?: readonly string[] } = {},
): Promise<string | null> {
  if (!/^[0-9a-f]{64}$/i.test(pubkey)) return null;
  const relays = ghostRelaySet(opts.relays);
  if (relays.length === 0) return null;

  const filter: Record<string, unknown> = {
    kinds: [GHOST_KIND],
    authors: [pubkey],
    since: Math.max(0, sinceSec - 30),
    limit: 5,
  };

  const events = await new Promise<NostrEvent[]>((resolve) => {
    const collected: NostrEvent[] = [];
    let done = 0;
    const sockets: WebSocket[] = [];
    let settled = false;
    const settle = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sockets.forEach((s) => { try { s.close(); } catch { /* ignore */ } });
      resolve(collected);
    };
    const timer = setTimeout(settle, FETCH_TIMEOUT_MS);
    const markDone = (): void => {
      done += 1;
      if (done >= relays.length) settle();
    };
    for (const url of relays) {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { markDone(); continue; }
      sockets.push(ws);
      const subId = 'gl' + Math.random().toString(36).slice(2, 10);
      ws.onopen = () => ws.send(JSON.stringify(['REQ', subId, filter]));
      ws.onmessage = (ev) => {
        try {
          const msg: unknown = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
          if (!Array.isArray(msg)) return;
          if (msg[0] === 'EVENT' && msg[1] === subId) {
            const e = msg[2];
            if (isGhostEvent(e)) collected.push(e);
          } else if (msg[0] === 'EOSE' && msg[1] === subId) {
            markDone();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = markDone;
      ws.onclose = markDone;
    }
  });

  // Most recent first — pick the newest that has a valid 'e' tag.
  events.sort((a, b) => b.created_at - a.created_at);
  for (const e of events) {
    if (!hasTagValue(e.tags, 'game', GAME_ID)) continue;
    const scoreEventId = tagValue(e.tags, 'e');
    if (scoreEventId && /^[0-9a-f]{64}$/i.test(scoreEventId)) return scoreEventId;
  }
  return null;
}

function isGhostEvent(value: unknown): value is NostrEvent {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  return e.kind === GHOST_KIND
    && typeof e.id === 'string'
    && typeof e.pubkey === 'string'
    && typeof e.content === 'string'
    && Array.isArray(e.tags);
}

function tagValue(tags: string[][], name: string): string | undefined {
  for (const t of tags) if (t[0] === name && typeof t[1] === 'string') return t[1];
  return undefined;
}

function hasTagValue(tags: string[][], name: string, value: string): boolean {
  for (const t of tags) if (t[0] === name && t[1] === value) return true;
  return false;
}

/** Pose the ghost ship was at by `t` ms into the run. Linear-interp between
 *  pose samples; returns null if the ghost has no pose stream (v1 events). */
export function ghostPoseAt(
  run: GhostRun,
  t: number,
): { x: number; y: number; rot: number; alive: boolean; thrusting: boolean } | null {
  const s = run.poseSamples;
  if (!s || s.length === 0) return null;
  if (t <= s[0].t) {
    const a = s[0];
    return { x: a.x, y: a.y, rot: a.rot, alive: (a.flags & 1) !== 0, thrusting: (a.flags & 2) !== 0 };
  }
  const last = s[s.length - 1];
  if (t >= last.t) {
    return { x: last.x, y: last.y, rot: last.rot, alive: (last.flags & 1) !== 0, thrusting: (last.flags & 2) !== 0 };
  }
  let lo = 0;
  let hi = s.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (s[mid].t <= t) lo = mid;
    else hi = mid;
  }
  const prev = s[lo];
  const next = s[hi];
  if (next.t === prev.t) {
    return { x: next.x, y: next.y, rot: next.rot, alive: (next.flags & 1) !== 0, thrusting: (next.flags & 2) !== 0 };
  }
  const frac = (t - prev.t) / (next.t - prev.t);
  // Hyperspace can teleport the ship across the field; if the gap looks like
  // a teleport (>200 px in 250ms = >800 px/s), snap rather than interp so the
  // ghost doesn't streak diagonally across the playfield.
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const teleport = dx * dx + dy * dy > 200 * 200;
  if (teleport) {
    return {
      x: frac < 0.5 ? prev.x : next.x,
      y: frac < 0.5 ? prev.y : next.y,
      rot: frac < 0.5 ? prev.rot : next.rot,
      alive: (next.flags & 1) !== 0,
      thrusting: (next.flags & 2) !== 0,
    };
  }
  // Interp rotation through the short way (handle ±π wrap).
  let dRot = next.rot - prev.rot;
  if (dRot > Math.PI) dRot -= Math.PI * 2;
  else if (dRot < -Math.PI) dRot += Math.PI * 2;
  return {
    x: prev.x + dx * frac,
    y: prev.y + dy * frac,
    rot: prev.rot + dRot * frac,
    alive: (next.flags & 1) !== 0,
    thrusting: (next.flags & 2) !== 0,
  };
}

/** Score the ghost was at by `t` ms into the run. Linear-interps between
 *  samples so the chip ticks smoothly rather than stepping once per second. */
export function ghostScoreAt(run: GhostRun, t: number): number {
  const s = run.samples;
  if (s.length === 0) return 0;
  if (t <= s[0].t) return s[0].score;
  const last = s[s.length - 1];
  if (t >= last.t) return last.score;
  // Binary search for the first sample with t > target.
  let lo = 0;
  let hi = s.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (s[mid].t <= t) lo = mid;
    else hi = mid;
  }
  const prev = s[lo];
  const next = s[hi];
  if (next.t === prev.t) return next.score;
  const frac = (t - prev.t) / (next.t - prev.t);
  return Math.round(prev.score + (next.score - prev.score) * frac);
}
